import {InteractionEvent, Point, Sprite} from 'pixi.js';
import {GameTable} from './game_scene/GameTable';
import {PieMenuHolder} from './game_scene/PieMenuHolder';
import {Hand} from './game_scene/Hand';
import {ActiveData} from '../data/ActiveData';
import {DynamicData} from '../data/DynamicData';
import {StaticData} from '../data/StaticData';
import {SceneCardsHolder} from './game_scene/SceneCardsHolder';
import {Tooltip} from './game_scene/Tooltip';
import {GameSceneEvents} from './game_scene/GameSceneEvents';
import {PixiApplicationController} from '../controllers/PixiApplicationController';
import {HotkeysController} from '../controllers/HotkeysController';
import {PlayerData} from '../data/active_data/field_config/PlayerData';
import {StacksHolder} from './game_scene/game_table/table_objects/StacksHolder';
import {StackEvents} from './game_scene/game_table/table_objects/stacks/stack/StackEvents';
import {Stack} from './game_scene/game_table/table_objects/stacks/Stack';
import {TableController} from '../controllers/silent/TableController';
import {DrawHolder} from './DrawHolder';
import {DrawController} from '../controllers/DrawController';

export class GameScene extends Sprite
{

    static hand: Hand;
    gameTable: GameTable;
    pieMenuHolder: PieMenuHolder;
    holdingStacks: StacksHolder = new StacksHolder();
    hand: Hand;
    cardsHolder: SceneCardsHolder;
    drawHolder: DrawHolder;
    private readonly tooltip: Tooltip;

    constructor() {
        super();

        HotkeysController.start();

        this.gameTable = new GameTable();
        this.hand = new Hand();
        GameScene.hand = this.hand;
        this.cardsHolder = new SceneCardsHolder();
        this.pieMenuHolder = new PieMenuHolder();
        this.tooltip = new Tooltip();
        this.drawHolder = new DrawHolder();

        this.addChild(this.gameTable);
        this.addChild(this.hand);
        this.addChild(this.holdingStacks);
        this.addChild(this.cardsHolder);
        this.addChild(this.tooltip);
        this.addChild(this.pieMenuHolder);
        this.addChild(this.drawHolder);

        PixiApplicationController.app.stage.on('pointermove', this.onPointerMove, this);
        addEventListener(GameSceneEvents.TOOLTIP_ON, this.onTooltipOn.bind(this));
        addEventListener(GameSceneEvents.TOOLTIP_OFF, this.onTooltipOff.bind(this));
        addEventListener(StackEvents.ON_STACK_POINTER_DOWN, this.onStackPointerDown.bind(this));
        addEventListener(StackEvents.ON_STACK_POINTER_UP, this.onStackPointerUp.bind(this));
        addEventListener(StackEvents.ON_STACK_TAKE_CARD, this.onStackTakeCard.bind(this));
        addEventListener(StackEvents.ON_STACK_PUT_CARD, this.onStackPutCard.bind(this));

        TableController.start(this.gameTable, this.holdingStacks);
        DrawController.start(this.drawHolder);
    }

    /**
     * Обработчик внутриигрового ивента начала взаимодействия с колодой
     * @param e Внутриигровое событие
     */
    async onStackPointerDown(e: Event): Promise<void> {
        let data: {stack: Stack} = (e as MessageEvent).data;
        let stack: Stack = data.stack;
        this.gameTable.tableObjects!.stacks.removeStack(stack);
        await this.holdingStacks.addStack(stack);
        stack.interactive = stack.interactiveChildren = false;
    }

    /**
     * Обработчик внутриигрового ивента окончания взаимодействия с колодой
     * @param e Внутриигровое событие
     */
    async onStackPointerUp(e: Event): Promise<void> {
        let data: {stack: Stack} = (e as MessageEvent).data;
        let stack: Stack = data.stack;
        this.holdingStacks.removeStack(stack);
        await this.gameTable.tableObjects!.stacks.addStack(stack, false);
        stack.interactive = stack.interactiveChildren = true;
        this.hand.backgroundAndCardsHolder.cardsHolder.makeTempCardsNotInteractive();
    }

    /**
     * Обработчик внутриигрового ивента взять карту из колоды
     * @param e Внутриигровое событие
     */
    onStackTakeCard(e: Event): void {
        let data: {stack: Stack} = (e as MessageEvent).data;
        let stack: Stack = data.stack;
        this.holdingStacks.addStack(stack);
    }

    /**
     * Обработчик внутриигрового ивента положить карту в колоду
     * @param e Внутриигровое событие
     */
    onStackPutCard(e: Event): void {
        let data: {stack: Stack} = (e as MessageEvent).data;
        let stack: Stack = data.stack;
        this.holdingStacks.removeStack(stack);
    }

    /**
     * Перерисовка при изменении размеров окна
     */
    resize(): void {
        this.hand.x = StaticData.sceneBorderThickness;
        this.hand.y = DynamicData.sceneSizes.height - DynamicData.handSizes.height - StaticData.sceneBorderThickness;
        this.hand.resize();
        this.gameTable.resize();
        this.cardsHolder.handPosition = this.hand.position;
        this.drawHolder.resize();
    }

    /**
     * Показать/скрыть руку
     */
    toggleHand(handIsOpened: boolean) {
        this.hand.visible = handIsOpened;
        if (StaticData.isAdmin) {
            this.hand.interactive = this.hand.interactiveChildren = false;
        }
    }

    async initialize(): Promise<void> {
        await this.gameTable.initialize();
        let data: PlayerData = ActiveData.root.getCurrentPlayerData();
        if (!data) {
            //Пока просто гостим
            return;
        }
        this.holdingStacks.initialize();
        await this.hand.initialize(data);
    }

    private onPointerMove(e: InteractionEvent): void {
        let position: Point = this.parent.toLocal(e.data.global, void 0);
        this.tooltip.position.set(position.x, position.y);
    }

    private onTooltipOff(): void {
        this.tooltip.visible = false;
    }

    private onTooltipOn(e: Event): void {
        let message: MessageEvent = e as MessageEvent;
        let data: {text: string, locked: boolean} = message.data;
        this.tooltip.setText(data.text);
        this.tooltip.setLock(data.locked);
        this.tooltip.visible = true;
    }
}
